import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { Seo } from "../components/Seo";
import { MapPin, Clock, ExternalLink, ArrowLeft, Compass, CalendarDays, ArrowRight, Lightbulb, Check } from "lucide-react";

const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
function nextFriday() {
  const d = new Date(); d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + ((5 - d.getDay() + 7) % 7 || 7)); // upcoming Friday
  return d;
}
function stayIdeas(lang) {
  const start = nextFriday();
  const mk = (nights) => { const a = new Date(start); const b = new Date(start); b.setDate(b.getDate() + nights); return { arrivee: fmt(a), depart: fmt(b) }; };
  const L = lang === "en"
    ? [["Weekend getaway", "2 nights"], ["Discovery break", "3 nights"], ["A week away", "7 nights"]]
    : [["Escapade week-end", "2 nuits"], ["Séjour découverte", "3 nuits"], ["Une semaine en immersion", "7 nuits"]];
  return [2, 3, 7].map((n, i) => ({ nights: n, label: L[i][0], sub: L[i][1], ...mk(n) }));
}

export default function RegionDetail() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const [item, setItem] = useState(null);
  const [nf, setNf] = useState(false);

  useEffect(() => {
    setItem(null); setNf(false);
    api.get(`/region/${slug}`).then((r) => setItem(r.data)).catch(() => setNf(true));
  }, [slug]);

  if (nf) return <div className="pt-40 pb-24 text-center"><Seo title="Lieu introuvable | La Bégaudière" path={`/la-region/${slug}`} lang={lang} noindex /><h1 className="font-heading text-3xl mb-4">Lieu introuvable</h1><Link to="/la-region" className="text-brand">{t("nav.region")}</Link></div>;
  if (!item) return <div className="pt-40 pb-40 text-center text-ink-400">{t("common.loading")}</div>;

  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(item.name + ", Bretagne, France")}&output=embed`;
  const desc = (item.description || "").replace(/\s+/g, " ").trim().slice(0, 160)
    || `${item.name} · à découvrir depuis le Domaine de la Bégaudière, baie du Mont-Saint-Michel.`;
  const touristSchema = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    name: item.name,
    description: desc,
    image: item.main_image,
    address: { "@type": "PostalAddress", addressRegion: "Bretagne", addressCountry: "FR" },
  };

  return (
    <div>
      <Seo title={`${item.name} | La Bégaudière`} description={desc}
        path={`/la-region/${slug}`} lang={lang} image={item.main_image} jsonLd={touristSchema} />
      <PageHero title={item.name} subtitle={t("regionPage.subtitle")} image={item.main_image} crumb={item.name} />
      <section className="py-14 lg:py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <Link to="/la-region" className="inline-flex items-center gap-2 text-ink-600 hover:text-brand mb-8 text-sm"><ArrowLeft size={16} /> {t("nav.region")}</Link>
          <Reveal><p className="text-ink-600 font-body text-lg leading-relaxed mb-8">{item.description}</p></Reveal>
          {item.tips && <Reveal><p className="text-ink-600 font-body leading-relaxed mb-8">{item.tips}</p></Reveal>}

          {Array.isArray(item.highlights) && item.highlights.length > 0 && (
            <Reveal>
              <div className="rounded-2xl bg-brand-50/50 border border-brand/15 p-6 sm:p-8 mb-10" data-testid="region-highlights">
                <h2 className="font-heading text-2xl text-ink-900 mb-5 flex items-center gap-2">
                  <Compass size={22} className="text-brand" /> {t("regionPage.thingsToDo")}
                </h2>
                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                  {item.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2.5 text-ink-700 font-body text-sm leading-relaxed">
                      <MapPin size={17} className="text-brand shrink-0 mt-0.5" /> {h}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          )}

          {Array.isArray(item.tips_list) && item.tips_list.length > 0 && (
            <Reveal>
              <div className="rounded-2xl bg-cream border border-black/5 p-6 sm:p-8 mb-10" data-testid="region-tips">
                <h2 className="font-heading text-2xl text-ink-900 mb-5 flex items-center gap-2">
                  <Lightbulb size={22} className="text-brand" /> {lang === "en" ? "Good to know" : "Bon à savoir"}
                </h2>
                <ul className="space-y-3">
                  {item.tips_list.map((tp) => (
                    <li key={tp} className="flex items-start gap-2.5 text-ink-700 font-body text-sm leading-relaxed">
                      <Check size={17} className="text-brand shrink-0 mt-0.5" /> {tp}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          )}

          <div className="flex flex-wrap gap-4 mb-10">
            {item.distance && <div className="flex items-center gap-2.5 rounded-xl bg-white border border-black/5 px-5 py-3"><MapPin size={18} className="text-brand" /><span className="text-sm text-ink-700">{t("regionPage.distance")}: {item.distance}</span></div>}
            {item.duration && <div className="flex items-center gap-2.5 rounded-xl bg-white border border-black/5 px-5 py-3"><Clock size={18} className="text-brand" /><span className="text-sm text-ink-700">{t("regionPage.duration")}: {item.duration}</span></div>}
            {item.official_url && <a href={item.official_url} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 rounded-xl bg-brand-50 px-5 py-3 text-brand text-sm hover:bg-brand-100"><ExternalLink size={18} />{t("regionPage.officialSite")}</a>}
          </div>

          <div className="rounded-2xl overflow-hidden border border-black/5 aspect-[16/9]">
            <iframe title={item.name} src={mapSrc} className="w-full h-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>

          <Reveal>
            <div className="mt-12 rounded-2xl p-6 sm:p-8" style={{ backgroundColor: "#0c3a52" }} data-testid="stay-ideas">
              <div className="flex items-center gap-2.5 mb-1.5">
                <CalendarDays size={20} className="text-white/90" />
                <h2 className="font-heading text-2xl text-white">{lang === "en" ? "Stay ideas" : "Idées de séjour"}</h2>
              </div>
              <p className="text-white/60 font-body text-sm mb-6">
                {lang === "en"
                  ? `Book your stay near ${item.name} in one click, dates pre-filled.`
                  : `Réservez votre séjour près de ${item.name} en un clic, dates pré-remplies.`}
              </p>
              <div className="grid sm:grid-cols-3 gap-4">
                {stayIdeas(lang).map((s) => (
                  <Link
                    key={s.nights}
                    to={`/disponibilites?arrivee=${s.arrivee}&depart=${s.depart}&voyageurs=2`}
                    data-testid={`stay-idea-${s.nights}`}
                    className="group flex flex-col rounded-xl bg-white/5 border border-white/10 p-5 hover:bg-white/10 transition-colors"
                  >
                    <span className="text-white/50 font-body text-xs uppercase tracking-wider">{s.sub}</span>
                    <span className="font-heading text-lg text-white mt-1">{s.label}</span>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-white/90 font-medium">
                      {lang === "en" ? "Check availability" : "Voir les disponibilités"}
                      <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
